import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
  DashboardSection,
  DateRange,
  DateRangePreset,
  TimeGranularity,
  TenantContext,
  AuthState,
} from '../types/dashboard';

/* ============================================================================
   UI State
   ============================================================================ */

export interface UIState {
  sidebarCollapsed: boolean;
  activeSection: DashboardSection;
  dateRange: DateRange;
  expandedPanels: Record<string, boolean>;
  mobileMenuOpen: boolean;
  notificationsOpen: boolean;
}

/* ============================================================================
   Store Interface
   ============================================================================ */

interface MonarchState {
  tenant: TenantContext | null;
  auth: AuthState;
  ui: UIState;
  loading: boolean;
  error: string | null;

  // Tenant
  setTenant: (tenant: TenantContext) => void;
  clearTenant: () => void;

  // Auth
  setAuth: (auth: Partial<AuthState>) => void;
  logout: () => void;

  // UI
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
  setActiveSection: (section: DashboardSection) => void;
  setDateRange: (range: Partial<DateRange>) => void;
  togglePanel: (panelId: string) => void;
  setMobileMenuOpen: (open: boolean) => void;
  setNotificationsOpen: (open: boolean) => void;

  // General
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

/* ============================================================================
   Defaults
   ============================================================================ */

const defaultAuth: AuthState = {
  userId: null,
  email: null,
  displayName: null,
  avatarUrl: null,
  role: 'viewer',
  isAuthenticated: false,
  sessionToken: null,
};

const defaultDateRange: DateRange = {
  start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
  end: new Date(),
  preset: '30d',
  granularity: 'day',
  compareEnabled: true,
};

const defaultUI: UIState = {
  sidebarCollapsed: false,
  activeSection: 'overview',
  dateRange: { ...defaultDateRange },
  expandedPanels: {},
  mobileMenuOpen: false,
  notificationsOpen: false,
};

/* ============================================================================
   Store
   ============================================================================ */

const useMonarchStore = create<MonarchState>()(
  devtools(
    persist(
      (set) => ({
        tenant: null,
        auth: { ...defaultAuth },
        ui: { ...defaultUI },
        loading: false,
        error: null,

        setTenant: (tenant) => set({ tenant }),
        clearTenant: () => set({ tenant: null }),

        setAuth: (partial) =>
          set((state) => ({
            auth: {
              ...state.auth,
              ...partial,
              isAuthenticated: partial.userId ? true : state.auth.isAuthenticated,
            },
          })),

        logout: () => set({ auth: { ...defaultAuth } }),

        setSidebarCollapsed: (collapsed) =>
          set((state) => ({ ui: { ...state.ui, sidebarCollapsed: collapsed } })),

        toggleSidebar: () =>
          set((state) => ({
            ui: { ...state.ui, sidebarCollapsed: !state.ui.sidebarCollapsed },
          })),

        setActiveSection: (section) =>
          set((state) => ({ ui: { ...state.ui, activeSection: section } })),

        setDateRange: (range) =>
          set((state) => ({
            ui: {
              ...state.ui,
              dateRange: { ...state.ui.dateRange, ...range },
            },
          })),

        togglePanel: (panelId) =>
          set((state) => ({
            ui: {
              ...state.ui,
              expandedPanels: {
                ...state.ui.expandedPanels,
                [panelId]: !state.ui.expandedPanels[panelId],
              },
            },
          })),

        setMobileMenuOpen: (open) =>
          set((state) => ({ ui: { ...state.ui, mobileMenuOpen: open } })),

        setNotificationsOpen: (open) =>
          set((state) => ({ ui: { ...state.ui, notificationsOpen: open } })),

        setLoading: (loading) => set({ loading }),
        setError: (error) => set({ error }),

        reset: () =>
          set({
            tenant: null,
            auth: { ...defaultAuth },
            ui: { ...defaultUI },
            loading: false,
            error: null,
          }),
      }),
      {
        name: 'monarch-dashboard-store',
        merge: (persisted, current) => {
          const p = (persisted ?? {}) as Partial<MonarchState>;
          const pui = (p.ui ?? {}) as Partial<UIState>;
          const pdr = (pui.dateRange ?? {}) as Partial<DateRange>;
          return {
            ...current,
            ...p,
            auth: { ...defaultAuth, ...(p.auth ?? {}) },
            ui: {
              ...defaultUI,
              ...pui,
              dateRange: {
                ...defaultDateRange,
                preset: pdr.preset ?? defaultDateRange.preset,
                granularity: pdr.granularity ?? defaultDateRange.granularity,
                compareEnabled: true, // Force on during migration (stale localStorage may have false)
                start: pdr.start ? new Date(pdr.start) : defaultDateRange.start,
                end: pdr.end ? new Date(pdr.end) : defaultDateRange.end,
              },
            },
          };
        },
        partialize: (state) => ({
          tenant: state.tenant,
          auth: {
            userId: state.auth?.userId ?? null,
            email: state.auth?.email ?? null,
            displayName: state.auth?.displayName ?? null,
            role: state.auth?.role ?? 'viewer',
            isAuthenticated: state.auth?.isAuthenticated ?? false,
          },
          ui: {
            sidebarCollapsed: state.ui?.sidebarCollapsed ?? false,
            activeSection: state.ui?.activeSection ?? 'overview',
            dateRange: state.ui?.dateRange
              ? {
                  preset: state.ui.dateRange.preset ?? '30d',
                  granularity: state.ui.dateRange.granularity ?? 'day',
                  compareEnabled: state.ui.dateRange.compareEnabled ?? true,
                  start: state.ui.dateRange.start ?? new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
                  end: state.ui.dateRange.end ?? new Date(),
                }
              : { ...defaultDateRange },
            expandedPanels: state.ui?.expandedPanels ?? {},
          },
        }),
      }
    ),
    { name: 'MonarchDashboardStore' }
  )
);

export default useMonarchStore;
